import React, { useState } from "react";
import {
  Box,
  Button,
  TextField,
  Typography,
  Paper,
  Snackbar,
  Alert,
  Link,
} from "@mui/material";
import { Refresh } from "@mui/icons-material"; // Import Refresh icon
import axios from "axios";
import { useTranslation } from "react-i18next";
import ReCAPTCHA from "react-google-recaptcha";
import { useNavigate } from "react-router-dom";
import { useTheme } from "@mui/material/styles";
import AppLogo from "../components/app/AppLogo";

function LoginPage({ onLogin }) {
  const { t, i18n } = useTranslation();
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "error",
  });
  const [attempts, setAttempts] = useState(0);
  const [captchaVerified, setCaptchaVerified] = useState(false);
  const [showCaptcha, setShowCaptcha] = useState(false);
  const navigate = useNavigate();
  const baseURL = process.env.REACT_APP_API_URL;
  const theme = useTheme();

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!username.trim()) {
      setSnackbar({
        open: true,
        message: t("Please enter username"),
        severity: "error",
      });
      return;
    }

    if (!password.trim()) {
      setSnackbar({
        open: true,
        message: t("Please enter password"),
        severity: "error",
      });
      return;
    }

    if (showCaptcha && !captchaVerified) {
      setSnackbar({
        open: true,
        message: t("Please complete the CAPTCHA"),
        severity: "error",
      });
      return;
    }

    try {
      const response = await axios.post(baseURL + "/api/token/", {
        username,
        password,
      });

      onLogin(response.data);
      navigate("/home", { replace: true });

      setSnackbar({
        open: true,
        message: t("Login successful"),
        severity: "success",
      });

      setAttempts(0);
      setCaptchaVerified(false);
      setShowCaptcha(false);
    } catch (error) {
      let rawMessage = error.response?.data?.detail;

      if (!rawMessage && error.message === "Network Error") {
        rawMessage = "NetworkError";
      }

      let translated = rawMessage;

      if (rawMessage === "Access Denied") {
        translated = t("AccessDenied");
      } else if (rawMessage === "Incorrect username or password") {
        translated = t("Incorrect username or password");
      } else if (rawMessage === "NetworkError") {
        translated = t("NetworkError");
      }

      setSnackbar({
        open: true,
        message: translated,
        severity: "error",
      });

      setAttempts((prev) => {
        const updatedAttempts = prev + 1;
        if (updatedAttempts >= 3) {
          setShowCaptcha(true);
        }
        return updatedAttempts;
      });
    }
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const toggleLanguage = () => {
    const newLang = i18n.language === "ar" ? "en" : "ar";
    i18n.changeLanguage(newLang);
    localStorage.setItem("language", newLang);
  };

  const handleCaptchaChange = (value) => {
    if (value) {
      setCaptchaVerified(true);
    }
  };

  // Function to reload the page
  const reloadPage = () => {
    window.location.reload(true);// Trigger deep reload
  };

  return (
    <Box
      display="flex"
      justifyContent="center"
      alignItems="center"
      minHeight="100vh"
      sx={{ position: "relative", overflow: "hidden" }}
    >
      <img
        src="/static/images/background.gif"
        alt="Background"
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          objectFit: "cover",
          zIndex: -1,
        }}
      />

      <Paper
        elevation={5}
        sx={{
          padding: 4,
          width: 350,
          textAlign: "center",
          zIndex: 1,
          backgroundColor: theme.palette.background.default,
        }}
      >
        {/* Reload Button Below the Form */}
        <Link
          variant="contained"
          color="primary"
          onClick={reloadPage}
          sx={{
            display: "flex",
            justifyContent: "left",
            alignItems: "center",
          }}
        >
          <Refresh sx={{ marginRight: 1 }} />
          {t("")}
        </Link>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 1 }}>
          <AppLogo height={90} />
        </Box>
        <Typography variant="h5" mb={2}>
          {t("Login")}
        </Typography>

        <Button
          variant="outlined"
          onClick={toggleLanguage}
          size="small"
          sx={{ mb: 2 }}
        >
          {i18n.language === "ar" ? "English" : "العربية"}
        </Button>

        <form onSubmit={handleSubmit}>
          <TextField
            label={t("Username")}
            variant="outlined"
            fullWidth
            margin="normal"
            value={username}
            onChange={(e) => setUsername(e.target.value)}
          />
          <TextField
            label={t("Password")}
            type="password"
            variant="outlined"
            fullWidth
            margin="normal"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
          />

          {showCaptcha && (
            <Box my={2}>
              <ReCAPTCHA
                sitekey="6Ld0RyUrAAAAAPIa9wt_yKWSSYdes-tpwjY79vM1"
                onChange={handleCaptchaChange}
              />
            </Box>
          )}

          <Button
            type="submit"
            variant="contained"
            color="warning"
            fullWidth
            sx={{ marginTop: 2 }}
          >
            {t("Login")}
          </Button>
        </form>

        <Link
          href="/forgot-password"
          variant="body2"
          sx={{ display: "block", marginTop: 2 }}
        >
          {t("Forgot password?")}
        </Link>

        
      </Paper>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

export default LoginPage;
